import { createRouter, createWebHistory } from 'vue-router'

import Login from '../views/Login.vue'

import AccountRecovery from '../views/AccountRecovery.vue'
import AccountActivation from '../views/AccountActivation.vue'
import Dashboard from '../views/Dashboard.vue'
import Transactions from '../views/Transactions'
import Wallet from '../views/Wallet.vue'
import Planning from '../views/Planning.vue'
import Settings from '../views/Settings.vue'
import Objectives from '../views/Objectives.vue'
import ProfileConfig from '../views/settings/ProfileConfig.vue'
import CategoryConfig from '../views/settings/CategoryConfig.vue'
import PreferencesConfig from '../views/settings/PreferencesConfig.vue'
import BankAccessConfig from '../views/settings/BankAccessConfig.vue'
import FamilyAccount from '../views/settings/FamilyAccount.vue'
import PageModel from '../views/PageModel.vue'
import Empresas from '../views/Empresas.vue'
import Report from '../views/Report.vue'
import TermsOfUse from '../views/TermsOfUse.vue'
import Ebooks from '../views/Ebooks.vue'

import PlanningOnboarding from '../views/PlanningOnboarding.vue'
import Clients from '../views/Clients.vue'
import Redirect from '../views/Redirect.vue'

import { mainStore } from '../store'

import auth from '../services/auth.service'
import request from '../common/request'
import verify from './util/verify'
import functions from './util/functions'
import variables from '../variables'

const beforeActiveRoutes = ['account-activation']

const getRoutes = () => {
    let routes = [
        {
            path: '/login',
            name: 'login',
            component: Login,
            beforeEnter: verify.ifNotAuthenticated,
        },
        {
            path: '/wl/login',
            name: 'wl-login',
            component: Login,
            beforeEnter: verify.ifNotAuthenticated,
        },
        {
            path: '/redirect',
            name: 'redirect',
            component: Redirect,
            beforeEnter: verify.ifAuthenticatedOrNot,
        },
        {
            path: '/clientes',
            name: 'clients',
            component: Clients,
            beforeEnter: verify.ifNotAuthenticated,
        },
        {
            path: '/account-recovery',
            name: 'account-recovery-request',
            component: AccountRecovery,
            beforeEnter: verify.ifNotAuthenticated,
        },
        {
            path: '/account-recovery/:token',
            name: 'account-recovery',
            component: AccountRecovery,
            beforeEnter: verify.ifNotAuthenticated,
        },
        {
            path: '/account-activation',
            name: 'account-activation',
            component: AccountActivation,
            beforeEnter: verify.ifAuthenticated,
        },
        {
            path: '/empresas/activation',
            name: 'activation-corporate-plan',
            component: Empresas,
        },
        {
            path: '/termos-de-uso',
            name: 'terms-of-use',
            component: TermsOfUse,
        },
        {
            path: '/ebooks/:id',
            name: 'ebooks',
            component: Ebooks,
        },
        {
            path: '/onboarding-planejamento',
            name: 'planning-onboarding',
            component: PlanningOnboarding,
        },
        {
            path: '/',
            name: 'container',
            component: PageModel,
            redirect: '/painel',
            children: [
                {
                    path: '/painel',
                    name: 'dashboard',
                    component: Dashboard,
                    beforeEnter: verify.ifAuthenticated,
                },
                {
                    path: '/movimentacoes',
                    name: 'transactions',
                    component: Transactions,
                    beforeEnter: verify.ifAuthenticated,
                },
                {
                    path: '/movimentacoes/:payment',
                    name: 'payment_transactions',
                    component: Transactions,
                    beforeEnter: verify.ifAuthenticated,
                },
                {
                    path: '/planejamento',
                    name: 'planning',
                    component: Planning,
                    beforeEnter: verify.ifAuthenticated,
                },
                {
                    path: '/carteiras',
                    name: 'wallet',
                    component: Wallet,
                    beforeEnter: verify.ifAuthenticated,
                },
                {
                    path: '/configuracoes',
                    redirect: '/configuracoes/perfil',
                    name: 'settings',
                    component: Settings,
                    children: [
                        {
                            path: '/configuracoes/perfil',
                            name: 'perfil',
                            component: ProfileConfig,
                            beforeEnter: verify.ifRouteIsUnLocked,
                        },
                        {
                            path: '/configuracoes/categorias',
                            name: 'categorias',
                            component: CategoryConfig,
                            beforeEnter: verify.ifAuthenticated,
                        },
                        {
                            path: '/configuracoes/preferencias',
                            name: 'preferencias',
                            component: PreferencesConfig,
                            beforeEnter: verify.ifAuthenticated,
                        },
                        {
                            path: '/configuracoes/conta-familia',
                            name: 'family-account',
                            component: FamilyAccount,
                            beforeEnter: verify.ifAuthenticated,
                        },
                        {
                            path: '/configuracoes/contas-automaticas',
                            name: 'bankaccess',
                            component: BankAccessConfig,
                            beforeEnter: verify.ifRouteIsUnLocked,
                        },
                        {
                            path: '/configuracoes/contas-automaticas/:bankAccessId',
                            name: 'bankaccess_sync',
                            component: BankAccessConfig,
                            beforeEnter: verify.ifRouteIsUnLocked,
                        },
                    ],
                },
                {
                    path: '/objetivos',
                    name: 'objectives',
                    component: Objectives,
                    beforeEnter: verify.ifAuthenticated,
                },
                {
                    path: '/relatorio',
                    name: 'relatorio',
                    component: Report,
                    beforeEnter: verify.ifAuthenticated,
                },
            ],
        },
    ]

    const containerIndex = routes.findIndex((route) => route.name === 'container')

    const { containerRoutes, defaultRoutes } = functions.getLimitedRoutes()

    routes.push(...defaultRoutes)
    routes[containerIndex].children.push(...containerRoutes)

    return routes
}

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes: getRoutes(),
})

router.beforeEach(async (to, from, next) => {
    const store = mainStore()

    const user = store.user
    const id = localStorage.getItem('id')

    functions.verifyUsage()

    store.toggleSidebar(false)

    if (!user._id && id) {
        await request
            .getById({
                collection: 'users',
                _id: id,
            })
            .then((user) => localStorage.setItem('currency', user.currency || 'BRL'))
            .catch((error) => {
                console.error(error)
                auth.logout()
            })
    }

    if (
        !store.user.skipTutorial &&
        !variables.tutorial.disableBasicTutorial &&
        to.name === 'dashboard' &&
        from.name !== 'planning'
    )
        next('/planejamento')
    next()
})

router.afterEach(async (to, from) => {
    const store = mainStore()

    const id = localStorage.getItem('id')

    if (!store.fetched && id) {
        const user = store.user
        store.WAITING(true)
        functions.setDate()

        let extraQueries = {}
        if (store.plannerWithFamily) extraQueries.user = store.mainUserId

        if (user._id) {
            try {
                if (!beforeActiveRoutes.includes(to.name)) {
                    await request.fetch(['categories', 'payments'])

                    await request.fetch({
                        collection: 'invoices',
                        queryParams: store._date,
                    })

                    const paymentId = functions.getPaymentId(to)

                    await request.fetch([
                        { collection: 'budgets', queryParams: store._date },
                        { collection: 'transactions', queryParams: store._date },
                        {
                            collection: 'invoices',
                            attr: 'invoicesWithoutFiltering',
                            queryParams: { ignoreInvoicePeriod: true, inicio: store._date.inicio },
                        },
                        { collection: 'objectives' },
                        { collection: 'bankaccesses' },
                        { collection: 'families' },
                        { collection: 'linkedtransactions' },
                        { collection: 'profiles' },
                        { collection: 'educational', subroute: '/trails', attr: 'trails' },
                        { collection: 'userconfigs' },
                        { collection: 'tasks' },
                    ])

                    const family = store.isOttoFamily

                    await request.fetch([
                        {
                            collection: 'balancesChart',
                            queryParams: { ...store._date, paymentId, family, ...extraQueries },
                        },
                        { collection: 'balances', queryParams: { ...store._date, family, ...extraQueries } },
                    ])

                    store.FETCHED()
                }
            } catch (e) {
                console.error(e)
            }
        } else {
            auth.logout()
        }
    } else if (to.name !== 'login') {
        await functions.resetStore(to, from)
    }

    if (store.waitingTime) store.WAITING()
})

export default router
